import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import SignedImage from "@/components/media/SignedImage";
import SignedAudio from "@/components/media/SignedAudio";
import { MoreVertical, Flag, Ban, QrCode } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import type { Profile } from "@/lib/profile/types";
import { getApp } from "@/lib/apps";
import type { AppId } from "@/lib/apps";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import BlockButton from "@/components/safety/BlockButton";
import ReportDialog from "@/components/safety/ReportDialog";
import MilestoneList from "@/components/milestones/MilestoneList";
import { useEndorsements } from "@/lib/endorsements/useEndorsements";
import { Heart } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import ProfileQrCard from "@/components/profile/ProfileQrCard";
import BgcStatusBadge, { bgcStatusFromProfile } from "@/components/safety/BgcStatusBadge";

const PublicProfile = () => {
  const { profileId } = useParams<{ profileId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const { summary: endorsementSummary } = useEndorsements(profile?.id);

  useEffect(() => {
    if (!profileId) return;
    let cancelled = false;
    setLoading(true);
    void supabase
      .from("public_profiles")
      .select("*")
      .eq("profile_id", profileId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setProfile((data as unknown as Profile | null) ?? null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!profile || profile.incognito) {
    return (
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        <SiteHeader />
        <main className="flex-1 mx-auto max-w-xl px-6 py-24 text-center">
          <h1 className="text-3xl font-bold tracking-tight">Profile not found</h1>
          <p className="mt-3 text-muted-foreground">
            This member may not exist or has hidden their profile.
          </p>
          <Button asChild className="mt-6">
            <Link to="/">Go home</Link>
          </Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const app = profile.active_app ? getApp(profile.active_app as AppId) : null;
  const photos = profile.photos ?? [];
  const main = photos[activePhoto] ?? photos[0];

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <main className="flex-1 mx-auto w-full max-w-3xl px-6 py-12">
        {app && (
          <Link
            to={`/${app.id}`}
            className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground hover:text-foreground"
          >
            <img src={app.icon} alt="" className="h-4 w-4" />
            {app.community}
          </Link>
        )}

        <div className="mt-6 grid gap-8 md:grid-cols-[1.2fr_1fr]">
          {/* Photo column */}
          <div>
            {main ? (
              <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-secondary">
                <SignedImage src={main} alt={profile.display_name ?? ""} className="h-full w-full object-cover" />
              </div>
            ) : (
              <div className="aspect-[4/5] rounded-2xl bg-secondary flex items-center justify-center text-muted-foreground text-sm">
                No photo
              </div>
            )}
            {photos.length > 1 && (
              <div className="mt-3 grid grid-cols-6 gap-2">
                {photos.map((url, i) => (
                  <button
                    key={url + i}
                    type="button"
                    onClick={() => setActivePhoto(i)}
                    className={`aspect-square overflow-hidden rounded-md ring-2 transition-all ${
                      i === activePhoto ? "ring-foreground" : "ring-transparent opacity-60 hover:opacity-100"
                    }`}
                  >
                    <SignedImage src={url} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details column */}
          <div className="space-y-6">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-3xl font-bold tracking-tight">
                  {profile.display_name}
                </h1>
                {profile.pronouns && (
                  <p className="text-sm text-muted-foreground mt-1">{profile.pronouns}</p>
                )}
                {profile.location_text && (
                  <p className="text-sm text-muted-foreground mt-1">{profile.location_text}</p>
                )}
                <div className="mt-2">
                  <BgcStatusBadge status={bgcStatusFromProfile(profile)} publicOnly size="md" />
                </div>
              </div>
              <div className="flex items-center gap-1">
                {profile.profile_id ? (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label="Show QR code">
                        <QrCode className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-sm">
                      <DialogHeader>
                        <DialogTitle>
                          {profile.display_name ?? "Profile"} · QR
                        </DialogTitle>
                      </DialogHeader>
                      <ProfileQrCard
                        profileId={profile.profile_id}
                        displayName={profile.display_name}
                      />
                    </DialogContent>
                  </Dialog>
                ) : null}
                {user && user.id !== profile.id && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label="Profile options">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <ReportDialog
                        targetUserId={profile.id}
                        trigger={
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                            <Flag className="h-4 w-4 mr-2" /> Report
                          </DropdownMenuItem>
                        }
                      />
                      <DropdownMenuSeparator />
                      <BlockButton
                        targetUserId={profile.id}
                        targetName={profile.display_name ?? undefined}
                        onBlocked={() => navigate("/")}
                        trigger={
                          <DropdownMenuItem
                            onSelect={(e) => e.preventDefault()}
                            className="text-destructive focus:text-destructive"
                          >
                            <Ban className="h-4 w-4 mr-2" /> Block
                          </DropdownMenuItem>
                        }
                      />
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>

            {profile.bio && (
              <p className="text-sm leading-relaxed">{profile.bio}</p>
            )}

            {profile.voice_intro_url && (
              <div className="rounded-xl border border-border p-4">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                  Voice intro
                </p>
                <SignedAudio src={profile.voice_intro_url} controls className="w-full" />
              </div>
            )}
          </div>
        </div>

        {/* Prompts */}
        {profile.prompts && profile.prompts.length > 0 && (
          <section className="mt-10 space-y-4">
            {profile.prompts.map((p) => (
              <div key={p.key} className="rounded-2xl border border-border bg-card p-5">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {p.text}
                </p>
                <p className="mt-2 text-base leading-relaxed">{p.answer}</p>
              </div>
            ))}
          </section>
        )}

        {/* Milestones */}
        <section className="mt-10">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
            Milestones
          </p>
          <MilestoneList userId={profile.id} emptyHint="No milestones yet." />
        </section>

        {/* Endorsements */}
        {endorsementSummary.length > 0 && (
          <section className="mt-10">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
              Endorsed for
            </p>
            <div className="flex flex-wrap gap-2">
              {endorsementSummary.slice(0, 12).map((e) => (
                <div
                  key={e.quality}
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/40 px-3 py-1.5 text-xs"
                >
                  <Heart className="h-3.5 w-3.5 text-primary" aria-hidden />
                  <span className="font-medium">{e.quality}</span>
                  <span className="text-muted-foreground">· {e.count}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

export default PublicProfile;
